import React, { useEffect, useState, Fragment } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { checkboxClasses } from '@mui/material/Checkbox';
import ClearIcon from '@mui/icons-material/HighlightOff';

import content from './content';
import { Loader } from '../../../common';
import { addItemsStyles } from './styles';
import { colors } from '../../../theme/color-palette';
import { BOGLE_LIGHT, BOGLE_MEDIUM } from '../../../constants/constant';
import {
	updateSelectedItem,
	updateCompareSelectedItemA,
	updateCompareSelectedItemB,
} from '../../../redux/actions/createRuleActions';

const tableStyles = {
	header: {
		fontSize: '16px',
		fontFamily: BOGLE_MEDIUM,
		color: colors.GREY,
	},
	cell: {
		padding: '0 0 0 16px',
		fontSize: '16px',
		fontFamily: BOGLE_LIGHT,
	},
};

const idAndNameKeyMap = {
	store: { id: 'storeId', name: 'storeName' },
	item: { id: 'itemId', name: 'itemName' },
	category: { id: 'catId', name: 'catName' },
	department: { id: 'deptId', name: 'deptName' },
	subcategory: { id: 'subCatId', name: 'subCatName' },
	upc: { id: 'wmUpcNbr', name: 'upcDesc' },
	brand: { id: 'brandId', name: 'brandName' },
};

const SearchResult = ({ type, isComparison, ...props }) => {
	const { searchItems } = useSelector((state) => state.createRuleReducer);
	const [count, setCount] = useState(0);

	const result = searchItems[type];
	return (
		<Fragment>
			<Typography
				id="modal-modal-title"
				variant="h6"
				component="h2"
				style={addItemsStyles.searchResults}>
				Search Results {count > 0 ? `(${count} selected)` : null}
			</Typography>
			<ResultTable
				type={type}
				result={result}
				setSelectedItemsCount={setCount}
				isComparison={isComparison}
				setDisableDoneButton={props?.setDisableDoneButton}
			/>
		</Fragment>
	);
};

SearchResult.propTypes = {
	type: PropTypes.string.isRequired,
	isComparison: PropTypes.string,
	setDisableDoneButton: PropTypes.func,
};

const ResultTable = ({ result, type, isComparison, ...props }) => {
	const dispatch = useDispatch();
	const { id, name } = idAndNameKeyMap[type];
	const t = content[type]?.add;
	const [resultItems, setResultItems] = useState([]);
	const { searchItems, selectedItems, selectedItemsA, selectedItemsB } =
		useSelector((state) => state.createRuleReducer);
	const selectedItemsArray = selectedItems[type] || [];
	const selectedItemsArrayA = selectedItemsA[type] || [];
	const selectedItemsArrayB = selectedItemsB[type] || [];
	const maxHeight =
		props.isBulkUpload && result.some(({ isValid }) => !isValid)
			? '290px'
			: '350px';

	useEffect(() => {
		setResultItems([
			...new Map(
				result?.map((item) => [
					item['catId'] ||
						item['storeId'] ||
						item['deptId'] ||
						item['itemId'] ||
						item['brandName'] ||
						item['wmUpcNbr'] ||
						item['subCatId'],
					item,
				])
			).values(),
		]);
	}, [result]);

	const checkBoxClickHandler = (ID, checked) => {
		// Update internal state values
		// Add the checked property to every item object
		// If check box is clicked and active the item is assigned `checked=true`
		const _resultItems = resultItems?.map((item) => {
			let _checked = item?.checked || false;

			if (type === 'brand' && item[name] === ID && _checked !== checked) {
				_checked = checked;
			} else if (item[id] === ID && _checked !== checked) {
				_checked = checked;
			}
			return {
				...item,
				checked: _checked,
			};
		});

		setResultItems(_resultItems);

		// Get the selected/checked items
		const _selectedItems = _resultItems.filter(({ checked }) => checked);

		// Set the Search Results count
		if (props.setSelectedItemsCount) {
			props.setSelectedItemsCount(_selectedItems.length);
		}

		updateItemsToStore(_selectedItems, { ID, checked });
		if (_selectedItems.length > 0) {
			props?.setDisableDoneButton(false);
		}
	};

	const updateItemsToStore = (input, opt) => {
		let finalSelectedItems = [];
		const items = getSelectedItems();

		if (opt.checked) {
			finalSelectedItems = items?.length
				? finalSelectedItems.concat(items)
				: [];

			input.forEach((item) => {
				if (
					type === 'brand' &&
					!finalSelectedItems.some((value) => value[name] === item[name])
				) {
					finalSelectedItems.push(item);
				} else if (
					!finalSelectedItems.some((value) => value[id] === item[id])
				) {
					finalSelectedItems.push(item);
				}
			});
		} else {
			items.forEach((item) => {
				if (item[id] !== opt?.ID) {
					finalSelectedItems.push(item);
				}
			});
		}
		if (isComparison === 'A') {
			dispatch(updateCompareSelectedItemA({ [type]: finalSelectedItems }));
		} else if (isComparison === 'B') {
			dispatch(updateCompareSelectedItemB({ [type]: finalSelectedItems }));
		} else {
			dispatch(updateSelectedItem({ [type]: finalSelectedItems }));
		}
	};

	const getSelectedItems = () => {
		if (isComparison === 'A') {
			return selectedItemsArrayA;
		} else if (isComparison === 'B') {
			return selectedItemsArrayB;
		} else {
			return selectedItemsArray;
		}
	};

	const removeItemHandler = (ID) => {
		const _resultItems = resultItems.filter((item) => item[id] !== ID);
		setResultItems(_resultItems);

		if (props.onRemoveItemClick) {
			props.onRemoveItemClick(ID);
		}
	};

	if (searchItems?.loading) {
		return <Loader show={true} height={'360px'} />;
	}

	return (
		<Fragment>
			{resultItems?.length > 0 && (
				<TableContainer style={{ marginTop: '16px', maxHeight }}>
					<Table aria-label="Search results table">
						<TableHead>
							<TableRow>
								{type !== 'brand' && (
									<TableCell sx={tableStyles.header}>
										{t['inputPlaceholder']}
									</TableCell>
								)}
								<TableCell sx={tableStyles.header} align="left">
									{t['item']}
								</TableCell>
								<TableCell sx={tableStyles.header} align="right"></TableCell>
							</TableRow>
						</TableHead>
						<TableBody sx={{ marginTop: '28px' }}>
							{resultItems?.map((row, key) => {
								if (props.isBulkUpload) {
									// For Bulk upload TWO types of items will appear, valid and invalid
									if (row?.isValid) {
										return (
											<TableRow
												key={key}
												sx={{
													background: row.checked
														? colors.TOGGLE_ACTIVE
														: colors.WHITE,
												}}>
												{type !== 'brand' && (
													<TableCell sx={tableStyles.cell} align="left">
														{row[id]}
													</TableCell>
												)}
												<TableCell sx={tableStyles.cell}>{row[name]}</TableCell>
												<TableCell sx={tableStyles.cell} align="right">
													<Checkbox
														sx={{
															[`&, &.${checkboxClasses.checked}`]: {
																color: colors.PURPLE,
															},
														}}
														checked={row.checked}
														inputProps={{ 'aria-label': 'checkbox' }}
														onChange={(...args) =>
															checkBoxClickHandler(row[id], args[1])
														}
													/>
												</TableCell>
											</TableRow>
										);
									} else {
										return (
											<TableRow
												key={key}
												sx={{
													background: colors.WHITE,
													color: colors.NEGATIVE_GROWTH_COLOR,
												}}>
												{type !== 'brand' && (
													<TableCell
														sx={{
															...tableStyles.cell,
															color: colors.NEGATIVE_GROWTH_COLOR,
														}}
														align="left">
														{row[id]}
													</TableCell>
												)}
												<TableCell sx={tableStyles.cell}>{row[name]}</TableCell>
												<TableCell
													sx={{
														...tableStyles.cell,
														color: colors.NEGATIVE_GROWTH_COLOR,
													}}
													align="right">
													<ClearIcon
														style={{ cursor: 'pointer', margin: '10px' }}
														onClick={() => removeItemHandler(row[id])}
													/>
												</TableCell>
											</TableRow>
										);
									}
								} else {
									return (
										<TableRow
											key={key}
											sx={{
												background: row.checked
													? colors.TOGGLE_ACTIVE
													: colors.WHITE,
											}}>
											{type !== 'brand' && (
												<TableCell sx={tableStyles.cell} align="left">
													{row[id]}
												</TableCell>
											)}
											<TableCell sx={tableStyles.cell}>{row[name]}</TableCell>
											<TableCell sx={tableStyles.cell} align="right">
												<Checkbox
													sx={{
														[`&, &.${checkboxClasses.checked}`]: {
															color: colors.PURPLE,
														},
													}}
													checked={row.checked}
													inputProps={{ 'aria-label': 'checkbox' }}
													onChange={(...args) =>
														checkBoxClickHandler(
															type === 'brand' ? row[name] : row[id],
															args[1]
														)
													}
												/>
											</TableCell>
										</TableRow>
									);
								}
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Fragment>
	);
};

ResultTable.propTypes = {
	result: PropTypes.array,
	isBulkUpload: PropTypes.bool,
	type: PropTypes.string.isRequired,
	onRemoveItemClick: PropTypes.func,
	setSelectedItemsCount: PropTypes.func,
	isComparison: PropTypes.string,
	setDisableDoneButton: PropTypes.func,
};

export { SearchResult, ResultTable };
